import { Component, Input, OnDestroy, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { ContextVerbiage, OnboardingVerbiage } from 'app/profile/models/supplier.model';
import { UserDetails } from 'app/profile/models/user.model';
import { CommonService } from 'app/profile/services/common.service';
import { GetSupplierProfileCurrentUser } from 'app/profile/store/app.actions';
import { OnboardingState } from 'app/profile/store/state/onboarding.state';
import { SupplierState } from 'app/profile/store/state/supplierstate.state';
import * as OnboardingActions from '../../../store/actions/onboarding.actions';
import * as ProfileSelectors from '../../../store/selectors/supplierstate.selector';
import * as MasterDataSelectors from "../../../store/selectors/masterdata.selector";
import * as MasterDataActions from "../../../store/actions/masterdata.action";
import { SupplierTaskService } from 'app/profile/services/supplier-task.service';
import { SupplierTaskDetail } from 'app/profile/models/suppliertasks.model';
import { AppSettings } from 'app/app.settings';
import { TaskStatus, TimeTrackTypes } from 'app/profile/models/onboarding.model';
import { TaskSidePanelComponent } from '../task-side-panel/task-side-panel.component';
import { MatDialog } from '@angular/material/dialog';
import { FileViewerComponent } from 'app/shared/components/file-viewer/file-viewer.component';
import { I18nService } from 'app/shared/services/i18n.service';
import { OTPService } from 'app/shared/services/otp/otp.service';
import { LanguageService } from 'app/core/services/language.service';
import { Subscription } from 'rxjs';
import { TslAccordion } from '@tesla/ng-tds';
import { BaseComponent } from '../../base.component';

@Component({
  selector: 'app-onboarding-home',
  templateUrl: './onboarding-home.component.html',
  styleUrls: ['./onboarding-home.component.scss'],
  encapsulation: ViewEncapsulation.None
})
export class OnboardingHomeComponent extends BaseComponent implements OnInit, OnDestroy {
  supplierName: string;
  supplierCode: string;
  supplierEmail: string;
  userDetails: UserDetails;
  userTaskList: SupplierTaskDetail[] = [];
  oldTaskList: SupplierTaskDetail[] = [];
  newTaskList: SupplierTaskDetail[] = [];
  completedNumber: number = 0;
  isloaded: boolean = false;
  onboardingType: 'extended' | 'initial' | 'subTier' | 'reactivation' | 'entityExtension' | 'nameOrTINChange' | 'TPRM';
  contextVerbiage: ContextVerbiage;
  isShowFAQ = false;
  isShowManual = false;
  languageSub: Subscription;
  taskId: string;
  allowToRerouteToPostOnboarding: boolean = false;
  entityCountry: string = null;
  tasksByRequest: any[] = [];

  statusClass = {
    'New': 'primary',
    'Completed': 'success',
    'Rejected': 'warning',
    'Pending': 'warning',
    'Under Tesla Review': 'success'
  }

  statusDisplay = {
    'New': 'New',
    'Completed': 'Completed',
    'Rejected': 'Needs Review',
    'Pending': 'Pending',
    'Under Tesla Review': 'Under Tesla Review'
  }

  onboardingTypeMap = {
    extended: 'TaskExtended',
    Extended: 'TaskExtended',
    initial: 'TaskInitial',
    Initial: 'TaskInitial',
    Reactivation: 'Reactivation',
    reactivation: 'Reactivation',
    subTier: 'SubTier',
    SubTier: 'SubTier',
  }
  verb: OnboardingVerbiage[];
  public language: string;

  constructor(
    private supplierStore: Store<SupplierState>,
    private onboardingStore: Store<OnboardingState>,
    private router: Router,
    private route: ActivatedRoute,
    private taskService: SupplierTaskService,
    private config: AppSettings,
    private commonService: CommonService,
    private matDialog: MatDialog,
    private translate: I18nService,
    private languageService: LanguageService,
    public otpService: OTPService
  ) { 
    super();
  }

  ngOnInit(): void {
    //  Get supplier code from route
    this.supplierCode = this.route.snapshot.params['code'];

    // get language
    this.languageSub = this.languageService.getSelectedLanguageAsync().subscribe(res => this.language = res || "en-US")

    if (this.supplierCode) {
      //  Dispatch action to save current supplier code
      this.onboardingStore.dispatch(OnboardingActions.SetCurrentSupplierCode({ supplierCode: this.supplierCode }));
    } else {
      //  Supplier code is required
      this.router.navigateByUrl('/');
    }

    //  Distpatch action to load currect user
    this.supplierStore.dispatch(new GetSupplierProfileCurrentUser());

    this.onboardingStore.dispatch(new MasterDataActions.GetContextVerbiage());

    this.supplierStore.select(ProfileSelectors.SelectCurrentUser).subscribe(userDetails => {
      if (userDetails) {
        this.userDetails = userDetails;
        this.supplierEmail = userDetails.supplierContact.emailId;

        this.allowToRerouteToPostOnboarding = this.commonService.allowToRerouteToPostOnboarding(userDetails, this.supplierCode);

        //  Find supplier data for the current supplier code
        let supplierMap = userDetails.supplierContact.supplierMaps.find(map => map.supplierCode == this.supplierCode);
        if (supplierMap) {
          this.supplierName = supplierMap.supplierName;
        }
        if (this.supplierCode) {
          if (this.commonService.isExtendedOnboardingState(this.userDetails, this.supplierCode)) this.onboardingType = 'extended';
          if (this.commonService.isInitialOnboardingState(this.userDetails, this.supplierCode)) this.onboardingType = 'initial';
          if (this.commonService.isSubTierOnboardingState(this.userDetails, this.supplierCode)) this.onboardingType = 'subTier';
          if (this.commonService.isReactivationState(this.userDetails, this.supplierCode)) this.onboardingType = 'reactivation';
          if (this.commonService.isEntityExtensionState(this.userDetails, this.supplierCode)) this.onboardingType = 'entityExtension';
          if (this.commonService.isNameTinChangeState(this.userDetails, this.supplierCode)) this.onboardingType = 'nameOrTINChange';
          this.setupVerbiage();
        }

        if(['entityExtension','nameOrTINChange'].includes(this.onboardingType))
        {
          this.PrimaryContactDocument(this.supplierStore, this.router, this.supplierCode, true, true);
        }
      }
    });

    this.onboardingStore.select(MasterDataSelectors.SelectContextVerbiage).subscribe(contextVerbiage => {
      if (!contextVerbiage) return;
      this.contextVerbiage = contextVerbiage;
      this.setupVerbiage();
    });

    this.isloaded = false;
    this.taskService.getSupplierTasks(this.supplierCode).subscribe(tasks => {
      this.userTaskList = tasks;
      this.oldTaskList = tasks;
      this.completedNumber = tasks.length;
      this.entityCountry = tasks[0].entityCountry;

      this.route.firstChild?.params?.subscribe(params => {
        if (params['taskId']) {
          this.taskId = params['taskId'];
          const task = this.userTaskList?.find(x => x.portalTaskID?.toString() == this.taskId);
          if (task && !task.disabled) {
            this.openTaskSideNav(task);
          } else {
            this.router.navigate(this.route.snapshot?.url?.map(x => x.path));
          }
        }
      });

      var requests = [...new Set(this.userTaskList.map(item => item.requestId))];
      this.tasksByRequest = requests?.map(requestId => {
        return { tasks: this.userTaskList.filter(x => x.requestId == requestId), requestType: this.userTaskList.filter(x => x.requestId == requestId)[0]?.requestType }
      });
      this.isloaded = true;
    });
  }

  setupVerbiage() {
    if (this.onboardingType) {
      let type = this.onboardingType;
      this.verb = this.contextVerbiage?.onboardingType[this.onboardingTypeMap[type]];
      this.isShowFAQ = this.verb?.find(x => x.type == 'FAQ')?.verbiage != null;
      this.isShowManual = this.verb?.find(x => x.type == 'Manual')?.verbiage != null;
    }
  }

  /** Event handler for when the user changes the supplier code from the drop-down */
  supplierCodeChangeEventHandler(newSupplierCode: string) {
    if (!newSupplierCode) {
      return;
    }

    //  Update supplier code field
    this.supplierCode = newSupplierCode;
    this.onboardingStore.dispatch(OnboardingActions.SetCurrentSupplierCode({ supplierCode: this.supplierCode }));

    if (this.userDetails && this.userDetails.supplierContact && this.userDetails.supplierContact.supplierMaps) {
      //  Find supplier data for the current supplier code
      let supplierMap = this.userDetails.supplierContact.supplierMaps.find(map => map.supplierCode == this.supplierCode);
      if (supplierMap) {
        this.supplierName = supplierMap.supplierName;
      }
    }

    if (this.commonService.isSubTierOnboardingState(this.userDetails, this.supplierCode)) {
      this.isloaded = false;
      this.taskService.getSupplierTasks(this.supplierCode).subscribe(tasks => {
        this.userTaskList = tasks;
        this.isloaded = true;
      });
    }
  }

  openTaskSideNavWithRouting(task: SupplierTaskDetail) {
    if (task.disabled) {
      return;
    }
    let routeList = this.route.snapshot?.url?.map(x => x.path);
    routeList.push(task.portalTaskID?.toString());
    this.router.navigate(routeList);
    this.openTaskSideNav(task);
  }

  openTaskSideNav(task: SupplierTaskDetail) {
    if (task.isSecure && !this.otpService.isAuthenticated()) {
      this.router.navigate(this.route.snapshot?.url?.map(x => x.path));
      var url = this.router.url;
      if (url && !url.includes(task?.portalTaskID?.toString())) {
        url = this.router.url + `/${task.portalTaskID}`;
      }
      this.otpService.requestAuthentication(url);
      return false;
    }
    let openTask = () => {
      if (task.taskStatus == TaskStatus.New || task.taskStatus == TaskStatus.Rejected) {
        this.startTimeTrack(task).subscribe(
          (response) => {
            // Handle the response from the server
            console.log('Time track started:', response);
          },
          (error) => {
            // Handle any errors that occurred during the request
            console.error('Failed to start time track:', error);
          }
        );
      }
      const dialogRef = this.matDialog.open(TaskSidePanelComponent, {
        panelClass: ['sidepanel', 'animate__animated', 'animate__slideInRight'],
        width: '65%',
        height: '100%',
        minWidth: '630px',
        data: { task: task, onboardingType: this.onboardingType }
      });

      dialogRef.afterClosed().subscribe((x) => {
        this.router.navigate(this.route.snapshot?.url?.map(x => x.path));
        this.refreshTaskBoard();
      });
    }

    openTask();
  }

  startTimeTrack(task: SupplierTaskDetail) {
    let timeTrackType = (task.taskStatus == TaskStatus.New || task.taskStatus == TaskStatus.Rejected) ? TimeTrackTypes.StartTime : null;
    return this.taskService.postTaskTimeTrack(task.portalTaskID.toString(), task.supplierCode, task.subTaskType, timeTrackType);
  }

  completedTimeTrack(task: SupplierTaskDetail) {
    let timeTrackType = TimeTrackTypes.CompletedTime;
    return this.taskService.postTaskTimeTrack(task.portalTaskID.toString(), task.supplierCode, task.subTaskType, timeTrackType);
  }

  refreshTaskBoard() {
    this.isloaded = false;
    this.taskService.getSupplierTasks(this.supplierCode).subscribe(tasks => {
      this.userTaskList = tasks;
      this.newTaskList = tasks;

      var requests = [...new Set(this.userTaskList.map(item => item.requestId))];
      this.tasksByRequest = requests?.map(requestId => {
        return { tasks: this.userTaskList.filter(x => x.requestId == requestId), requestType: this.userTaskList.filter(x => x.requestId == requestId)[0]?.requestType }
      });

      this.isloaded = true;
    });
  }

  openSRMTask() {
    var url = `${this.config.SRM_UI_URL}/tasks?taskTypeName=OnboardingTask%20Type`;
    if (this.onboardingType == 'reactivation') {
      url = `${this.config.SRM_UI_URL}/tasks?taskTypeName=ReactivationTask`;
    }
    window.open(url, '_blank').focus();
  }

  getProgress(tasks: SupplierTaskDetail[]) {
    let progressValue = 0;
    if (tasks) {
      progressValue = (tasks.filter(x => x.taskStatus === TaskStatus.Completed).length / tasks.length) * 100;
      if (this.completedNumber != this.userTaskList.filter(x => x.taskStatus === TaskStatus.Completed).length
        && this.oldTaskList.filter(x => x.taskStatus === TaskStatus.Completed).length
        != this.newTaskList.filter(x => x.taskStatus === TaskStatus.Completed).length) {
        this.completedNumber = this.userTaskList.filter(x => x.taskStatus === TaskStatus.Completed).length;
        this.saveTaskStatus();
      }
    }
    return Math.floor(progressValue);
  }

  showDocument(type) {
    const manual = this.verb?.find(x => x.type == type)?.verbiage?.userManual;
    if (manual) {
      this.showFile(manual);
    }
  }

  showFile(manual) {
    this.matDialog.open(FileViewerComponent, {
      panelClass: ['sidepanel', 'animate__animated', 'animate__slideInRight'],
      width: '65%',
      height: '100%',
      minWidth: '630px',
      data: {
        fileName: manual.fileName,
        fileId: manual.fileId,
        fileVersion: manual.fileVersion
      }
    });
  }

  ngOnDestroy(): void {
    if (this.languageSub) this.languageSub.unsubscribe();
  }

  saveTaskStatus() {
    if (this.oldTaskList.length > 0) {
      this.taskService.getSupplierTasks(this.supplierCode).subscribe(tasks => {
        this.newTaskList = tasks;
      });
      let updateTaskList = false;
      this.oldTaskList.forEach(task => {
        let newStatus = this.newTaskList.find(x => x.portalTaskID == task.portalTaskID && x.subTaskType == task.subTaskType);
        if (newStatus?.taskStatus != task.taskStatus && newStatus?.taskStatus == TaskStatus.Completed) {
          this.completedTimeTrack(task).subscribe(
            (response) => {
              // Handle the response from the server
              console.log('Completed Time track started:', response);
            },
            (error) => {
              // Handle any errors that occurred during the request
              console.error('Failed to Completed time track:', error);
            }
          );
          updateTaskList = true;
        }
      });
      if (updateTaskList) {
        this.oldTaskList = this.newTaskList;
      }
    }
    else {
      this.taskService.getSupplierTasks(this.supplierCode).subscribe(tasks => {
        this.oldTaskList = tasks;
      });
    }
  }

  navigateToDetail() {
    this.router.navigate([this.supplierCode])
  }

  isUnderReview(item: any): boolean {
    if (['TPRM', 'RRT'].includes(item?.requestType ?? '')
      && item?.tasks?.every(x => x.approvalStatus == "Approved")) {
      return false;
    }
    else {
      return true;
    }
  }
}
